(() => {
  if (customElements.get('vg-search')) return;

  class VargstenSearch extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.header = this.closest('[data-vg-header]');
      this.toggle = this.header.querySelector('[data-vg-search-toggle]');
      this.panel = this.querySelector('.vg-search-panel');
      this.backdrop = this.querySelector('[data-vg-search-close]');
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.clear = this.querySelector('[data-vg-search-clear]');
      this.suggestions = this.querySelector('[data-vg-search-suggestions]');
      this.results = this.querySelector('.vg-search-results');
      this.status = this.querySelector('[role="status"]');
      this.all = this.querySelector('[data-vg-search-all]');
      this.cache = new Map();
      this.sequence = 0;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };

      this.toggle.addEventListener('click', () => this.panel.hidden ? this.open() : this.close(), options);
      this.backdrop.addEventListener('click', () => this.close(), options);
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.update();
        this.input.focus();
      }, options);
      this.input.addEventListener('input', () => this.update(), options);
      this.input.addEventListener('search', () => this.update(), options);
      this.input.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' && !this.results.hidden) {
          const first = this.results.querySelector('a');
          if (first) { event.preventDefault(); first.focus(); }
        }
      }, options);
      this.form.addEventListener('submit', event => {
        this.input.value = this.input.value.trim();
        if (!this.input.value) { event.preventDefault(); this.input.focus(); }
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.panel.hidden) {
          event.preventDefault();
          this.close();
        }
      }, options);
      document.addEventListener('focusin', event => {
        if (!this.panel.hidden && !this.header.contains(event.target)) this.close(false);
      }, options);
      this.header.addEventListener('click', event => {
        if (event.target.closest('[data-vg-open]')) this.close(false);
      }, options);
      this.header.querySelectorAll('[data-vg-menu]').forEach(menu => {
        menu.addEventListener('toggle', () => { if (menu.open) this.close(false); }, options);
      });
    }

    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      this.controller = null;
    }

    message(key, fallback, values = {}) {
      return (this.dataset[key] || fallback).replace(/\{\{\s*(\w+)\s*\}\}|__(\w+)__/g, (_, braceName, markerName) => values[braceName || markerName] ?? '');
    }

    open() {
      this.header.querySelectorAll('[data-vg-menu]').forEach(menu => { menu.open = false; });
      this.panel.hidden = false;
      this.backdrop.hidden = false;
      this.header.classList.add('is-search-open');
      document.body.classList.add('vg-search-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', this.message('close', 'Close search'));
      this.toggle.title = this.message('close', 'Close search');
      this.toggle.querySelector('[data-vg-search-icon]').textContent = 'close';
      this.input.focus({ preventScroll: true });
      this.update();
    }

    close(restoreFocus = true) {
      this.cancel();
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.backdrop.hidden = true;
      this.header.classList.remove('is-search-open');
      document.body.classList.remove('vg-search-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', this.message('search', 'Search'));
      this.toggle.title = this.message('search', 'Search');
      this.toggle.querySelector('[data-vg-search-icon]').textContent = 'search';
      if (restoreFocus) this.toggle.focus({ preventScroll: true });
    }

    cancel() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence++;
      this.results?.setAttribute('aria-busy', 'false');
    }

    update() {
      this.cancel();
      const query = this.input.value.trim();
      this.clear.hidden = !this.input.value;
      this.suggestions.hidden = Boolean(query);
      this.results.replaceChildren();
      this.results.hidden = true;
      this.all.hidden = !query;
      this.status.classList.remove('vg-search-status--quiet');
      this.status.textContent = '';
      if (!query || this.panel.hidden) return;
      const url = new URL(this.form.action, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      this.all.href = url.href;
      this.status.textContent = this.message('searching', 'Searching...');
      this.results.setAttribute('aria-busy', 'true');
      const sequence = this.sequence;
      this.timer = setTimeout(() => this.search(query, sequence), 250);
    }

    async search(query, sequence) {
      try {
        let html = this.cache.get(query);
        if (html === undefined) {
          this.request = new AbortController();
          const url = new URL(this.dataset.predictiveUrl, location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('section_id', 'vg-predictive-search');
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          const response = await fetch(url.href, { signal: this.request.signal });
          if (!response.ok) throw new Error('Search unavailable');
          html = await response.text();
        }
        if (sequence !== this.sequence || this.panel.hidden) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-vg-search-response]');
        if (!content) throw new Error('Search results missing');
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, html);
        const count = Number(content.dataset.count) || 0;
        this.results.replaceChildren(...content.childNodes);
        this.results.hidden = count === 0;
        this.status.textContent = count ? this.message('suggestionCount', '{{ count }} product suggestions for {{ terms }}.', { count, terms: query }) : this.message('noRings', 'No rings found for "{{ terms }}".',  { terms: query });
        this.status.classList.toggle('vg-search-status--quiet', count > 0);
      } catch (error) {
        if (sequence !== this.sequence || this.panel.hidden || error.name === 'AbortError') return;
        this.status.textContent = this.message('unavailable', 'Suggestions are unavailable. View all results to continue your search.');
      } finally {
        if (sequence === this.sequence) this.results.setAttribute('aria-busy', 'false');
      }
    }
  }

  customElements.define('vg-search', VargstenSearch);
})();
